// lightweight global save/sync status.
//
// A tiny pub/sub replacing the ad-hoc per-component typing/sync refs that
// already exist in the editors. Cloud writes go through a small handful of
// functions in src/handlers/Sync/index.js; those are instrumented to
// transition the global status. The `SaveStatusChip` in the header subscribes
// to it via `useSyncStatus()`.
//
// Status values:
//   idle    — no edits pending, nothing happening.
//   dirty   — the user has queued a write but we haven't hit the network yet.
//   saving  — at least one in-flight write.
//   saved   — the most recent write completed successfully.
//   error   — the most recent write failed.
//
// The store is intentionally flat so it can be read synchronously and shared
// across React and non-React code.

const listeners = new Set();

const state = {
  status: "idle",
  pending: 0,
  lastSavedAt: 0,
  lastError: null,
};

function emit() {
  for (const listener of listeners) {
    try {
      listener({ ...state });
    } catch (error) {
      // A single faulty subscriber must not break the others.
      console.error("syncStatus listener threw", error);
    }
  }
}

function setStatus(next) {
  if (state.status === next) return;
  state.status = next;
  emit();
}

export function getSyncStatus() {
  return { ...state };
}

export function subscribeSyncStatus(listener) {
  listeners.add(listener);
  listener({ ...state });
  return () => {
    listeners.delete(listener);
  };
}

export function markDirty() {
  if (state.status === "saving") return;
  setStatus("dirty");
}

// Call at the start of a cloud write. Returns an `end(error?)` callback that
// must be invoked when the write resolves.
export function beginSave() {
  state.pending += 1;
  state.lastError = null;
  setStatus("saving");

  let finalized = false;
  return (error) => {
    if (finalized) return;
    finalized = true;

    state.pending = Math.max(0, state.pending - 1);

    if (error) {
      state.lastError = error;
      setStatus("error");
      return;
    }

    if (state.pending === 0) {
      state.lastSavedAt = Date.now();
      setStatus("saved");
    }
  };
}

// Wraps a promise-returning function so callers don't have to manage the
// begin/end pair manually.
export function trackSave(fn) {
  return async (...args) => {
    const end = beginSave();
    try {
      const value = await fn(...args);
      end();
      return value;
    } catch (error) {
      end(error);
      throw error;
    }
  };
}

export function resetSyncStatus() {
  state.status = "idle";
  state.pending = 0;
  state.lastSavedAt = 0;
  state.lastError = null;
  emit();
}
